import Axios from './axios';

export interface BucketUrlResponse {
  data: string;
}

export interface UploadImageResponse {
  status: number;
  data?: any;
}

export interface BucketFileUrlResponse {
  data: string;
}

/**
 * Get signed URL for image upload
 * @param data - Object containing uuid and image type
 * @returns Promise with bucket URL response
 */
export const getBucketUrlImageV2 = async (data: {
  uuid: string;
  type: string;
}): Promise<BucketUrlResponse> => {
  try {
    const map = {
      uuid: data.uuid,
      image_type: data.type,
    };
    const response = await Axios.post(`/v2/upload/image`, map);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * True when `url` points at Google Cloud Storage (our upload buckets), i.e. a
 * file that lives in GCS — either the shared temp bucket (just uploaded) or the
 * entity-scoped main bucket (already persisted).
 *
 * Legacy handover documents store an arbitrary external link (Google Drive, a
 * shared xlsx/docx, etc.) that is NOT in GCS. The backend's move-to-main-bucket
 * step can't relocate those, so the FE must treat them as "needs re-upload"
 * rather than as an already-uploaded file.
 */
export const isGcsUrl = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    return (
      hostname === 'storage.googleapis.com' ||
      hostname.endsWith('.storage.googleapis.com') ||
      hostname === 'storage.cloud.google.com'
    );
  } catch {
    return false;
  }
};

export const getBucketUrlFile = async (data: {
  uuid: string;
  fileType: string;
  fileFormat: string;
}): Promise<BucketFileUrlResponse> => {
  try {
    const map = {
      uuid: data.uuid,
      file_type: data.fileType,
      file_format: data.fileFormat,
    };
    const response = await Axios.post('/v1/upload/file', map);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload image file to temporary storage
 * @param url - Upload URL from getBucketUrlImageV2
 * @param imageBuffer - Base64 image data
 * @param fileName - Name of the file
 * @returns Promise with upload response
 */
export const uploadFileImage = async (
  url: string,
  imageBuffer: string,
  fileName: string
): Promise<UploadImageResponse> => {
  try {
    const formData = new FormData();

    const mimeType = 'image/png';

    const byteCharacters = atob(imageBuffer);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: mimeType });

    const file = new File([blob], fileName, { type: mimeType });
    formData.append('file', file);
    formData.append('url', url);

    const response = await Axios.put('/v1/upload/temp', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadTempFile = async (
  url: string,
  fileBuffer: string,
  fileName: string,
  mimeType: string
): Promise<UploadImageResponse> => {
  try {
    const formData = new FormData();

    const byteCharacters = atob(fileBuffer);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: mimeType });

    const file = new File([blob], fileName, { type: mimeType });
    formData.append('file', file);
    formData.append('url', url);

    const response = await Axios.put('/v1/upload/temp', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadFileDocument = async (
  url: string,
  fileBuffer: string,
  fileName: string,
  mimeType: string
): Promise<UploadImageResponse> => {
  try {
    const formData = new FormData();

    const byteCharacters = atob(fileBuffer);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: mimeType });

    const file = new File([blob], fileName, { type: mimeType });
    formData.append('file', file);
    formData.append('url', url);

    const response = await Axios.put('/v1/upload/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};
